import axios from "axios";
import {
  type BaseIntegrationConfigs,
  type GetUsersEmailsQueryParams,
  type GoogleIntegrationConfigs,
  type Integration,
  IntegrationTypes,
  type MsGraphIntegrationConfigs,
  type WebexIntegrationConfigs,
} from "../Integrations/types";
import { type SfIntegrationConfigs } from "../Integrations/types/successFactors";

import isEqual from "lodash/isEqual";
import { serializeArrayAndFilterNullable } from "utils/queryUtils";
import enrollmentRulesUtils from "../../../utils/enrollmentRulesUtils";
import { defaultRule } from "../Integrations/Configuration/MsGraphIntegrationFilterBuilder/helpers";
import { type VivaIntegrationConfigs } from "../Integrations/types/viva";

export const integrationApiUrls = {
  root: "/api/accounts/integrations",
  msgraph: `api/integrations/${IntegrationTypes.MsGraph}`,
  google: `api/integrations/${IntegrationTypes.Google}`,
  webex: `api/integrations/${IntegrationTypes.Webex}`,
  viva: `api/integrations/${IntegrationTypes.Viva}`,
  successfactors: `api/integrations/${IntegrationTypes.SuccessFactors}`,
};

const service = {
  async authorizeMsGraphIntegration(
    syncUsers: boolean,
    importUsageData: boolean,
    importLicenses: boolean,
    importGroups: boolean,
  ) {
    const response = await axios.post(`${integrationApiUrls.msgraph}/authorize`, {
      syncUsers,
      importUsageData,
      importLicenses,
      importGroups,
    });

    return response.data;
  },

  async authorizeGoogleIntegration(syncUsers: boolean, importUsageData: boolean, redirect: string) {
    const response = await axios.post(`${integrationApiUrls.google}/authorize`, {
      syncUsers,
      importUsageData,
      redirect,
    });

    return response.data;
  },

  async authorizeWebexIntegration(syncUsers: boolean, importUsageData: boolean, redirect: string) {
    const response = await axios.post(`${integrationApiUrls.webex}/authorize`, {
      syncUsers,
      importUsageData,
      redirect,
    });

    return response.data;
  },

  async authorizeVivaIntegration(redirect: string) {
    const response = await axios.post(`${integrationApiUrls.viva}/authorize`, {
      redirect,
    });

    return response.data;
  },

  async updateMsGraphIsActive(isActive: boolean) {
    return axios.put(`${integrationApiUrls.msgraph}/is-active`, {
      isActive,
    });
  },

  async getIntegrationSettings(MoboId?: string): Promise<Array<Integration>> {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    const response = await axios.get(integrationApiUrls.root, headers);
    return response.data;
  },

  async deleteIntegrations(types: string[], MoboId?: string) {
    const headers = MoboId
      ? {
          headers: { MoboId },
          data: {
            types,
          },
        }
      : {
          data: {
            types,
          },
        };

    await axios.delete(integrationApiUrls.root, headers);
  },

  async updateIntegrationsIsActiveState(types: string[], isActive: boolean, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    await axios.put(
      `${integrationApiUrls.root}/state`,
      {
        isActive,
        types,
      },
      headers,
    );
  },

  async createMsGraphIntegration(configs: MsGraphIntegrationConfigs, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;
    if (isEqual(configs.rules, [defaultRule])) delete configs.rules;
    if (configs.rules && configs.rules[0].value) {
      configs.rules = enrollmentRulesUtils.mapRulesToSeverFormat(configs.rules);
    }
    return axios.post(integrationApiUrls.msgraph, configs, headers);
  },

  async createGoogleIntegration(configs: GoogleIntegrationConfigs, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.post(integrationApiUrls.google, configs, headers);
  },

  async createWebexIntegration(configs: WebexIntegrationConfigs, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.post(integrationApiUrls.webex, configs, headers);
  },

  createVivaIntegration(configs: VivaIntegrationConfigs, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.post(integrationApiUrls.viva, configs, headers);
  },

  getIntegrationConfigs<T extends BaseIntegrationConfigs>(type: IntegrationTypes, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.get<T>(integrationApiUrls[type], headers);
  },

  updateIntegrationConfigs<T>(type: IntegrationTypes, configs: T, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.put(
      integrationApiUrls[type],
      {
        ...configs,
      },
      headers,
    );
  },

  createSfIntegration(configs: SfIntegrationConfigs, MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.post(integrationApiUrls.successfactors, configs, headers);
  },

  triggerMsSync(MoboId?: string) {
    const headers = MoboId
      ? {
          headers: {
            MoboId,
          },
        }
      : undefined;

    return axios.post(`${integrationApiUrls.msgraph}/sync`, undefined, headers);
  },
  async getManagers() {
    return (await axios.get(`${integrationApiUrls.msgraph}/managers`)).data;
  },
  async getDepartments() {
    return (await axios.get(`${integrationApiUrls.msgraph}/departments`)).data;
  },
  async getJobTitles() {
    return (await axios.get(`${integrationApiUrls.msgraph}/job-titles`)).data;
  },
  async getOfficeLocations() {
    return (await axios.get(`${integrationApiUrls.msgraph}/office-locations`)).data;
  },
  async getCountries() {
    return (await axios.get(`${integrationApiUrls.msgraph}/countries`)).data;
  },
  async getCompanyNames() {
    return (await axios.get(`${integrationApiUrls.msgraph}/company-names`)).data;
  },
  async getUsersEmails(queryParams: GetUsersEmailsQueryParams) {
    const response = await axios.get(`${integrationApiUrls.msgraph}/users-emails`, {
      params: queryParams,
      paramsSerializer: serializeArrayAndFilterNullable,
    });
    return response.data;
  },
};

export default service;
